import React, { useState } from 'react';
import { Container, Card, Button, Alert, Spinner, Form, Modal } from 'react-bootstrap';
import { PageLoader } from '../../Components/LoadingSpinner';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { Plus } from 'lucide-react';
import { useNotes } from '../../hooks/notes';
import { NoteItem } from '../../Components/NoteItem';
import './ProfilePage.css';
import './PersonalNotesPage.css';

const PersonalNotesPage: React.FC = () => {
    const [user, loading, error] = useAuthState(auth);

    // Set document title
    useDocumentTitle('Mina anteckningar');

    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
    const [newNoteText, setNewNoteText] = useState('');

    const {
        notesList,
        loading: notesLoading,
        error: notesError,
        submitting,
        addNote,
        updateNote,
        deleteNote,
        clearError,
    } = useNotes({ userId: user?.uid });

    const handleAddNote = async () => {
        if (!newNoteText.trim()) return;

        try {
            await addNote(newNoteText);
            setNewNoteText('');
            setShowAddModal(false);
        } catch (err) {
            // Error handled by hook
        }
    };

    const handleDeleteConfirm = () => {
        if (showDeleteModal) {
            deleteNote(showDeleteModal);
            setShowDeleteModal(null);
        }
    };

    if (loading) {
        return (
            <div className="profile-page">
                <PageLoader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="profile-page">
                <Container className="profile-page-container">
                    <Alert variant="danger">
                        <Alert.Heading>Fel vid laddning</Alert.Heading>
                        <p>{error.message}</p>
                    </Alert>
                </Container>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="profile-page">
                <Container className="profile-page-container">
                    <Alert variant="warning">
                        <Alert.Heading>Inte inloggad</Alert.Heading>
                        <p>Du måste vara inloggad för att komma åt denna sida.</p>
                    </Alert>
                </Container>
            </div>
        );
    }

    return (
        <div className="profile-page">
            <Container className="profile-page-container" style={{ maxWidth: '800px' }}>
                <div className="notes-header-section">
                    <div className="notes-header">
                        <div>
                            <h1 className="page-title">Mina anteckningar</h1>
                            <p className="notes-header-description">
                                Dessa anteckningar är endast synliga för dig.
                            </p>
                        </div>
                        <Button
                            variant="primary"
                            onClick={() => setShowAddModal(true)}
                        >
                            <Plus size={18} className="me-1" />
                            Lägg till
                        </Button>
                    </div>
                </div>

                {notesError && (
                    <Alert variant="danger" dismissible onClose={clearError} className="mb-3">
                        {notesError}
                    </Alert>
                )}

                {notesLoading ? (
                    <div className="text-center mt-5">
                        <Spinner animation="border" />
                    </div>
                ) : notesList.length === 0 ? (
                    <Card className="notes-empty-card dropshadow">
                        <Card.Body className="notes-empty-card-body">
                            <p className="notes-empty-text">Inga anteckningar att visa</p>
                        </Card.Body>
                    </Card>
                ) : (
                    <div className="notes-list">
                        {notesList.map((note) => (
                            <NoteItem
                                key={note.key}
                                note={note}
                                onEdit={(newText) => updateNote(note.key, note, newText)}
                                onDelete={() => setShowDeleteModal(note.key)}
                                submitting={submitting}
                            />
                        ))}
                    </div>
                )}

                {/* Add Note Modal */}
                <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Lägg till anteckning</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={newNoteText}
                            onChange={(e) => setNewNoteText(e.target.value)}
                            placeholder="Skriv din anteckning..."
                            autoFocus
                            disabled={submitting}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setShowAddModal(false)}
                            disabled={submitting}
                        >
                            Avbryt
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleAddNote}
                            disabled={submitting || !newNoteText.trim()}
                        >
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Sparar...
                                </>
                            ) : (
                                'Lägg till'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Delete confirmation modal */}
                <Modal show={showDeleteModal !== null} onHide={() => setShowDeleteModal(null)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Radera anteckning</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Är du säker på att du vill radera denna anteckning?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(null)}>
                            Avbryt
                        </Button>
                        <Button variant="danger" onClick={handleDeleteConfirm}>
                            Radera
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </div>
    );
};

export default PersonalNotesPage;
